import { stationDisplaysCurrency } from "../utils/currencyDisplayMode";
import { useMemo } from "react";

const TF_FUELS = new Set(["TF cig", "TF zboží"]);
const KURZ_GROUP = new Set(["Kurz", "TF zboží", "TF cig"]);

const fuelClass = (value) =>
  `fuel-${String(value).toLowerCase().replace(/\s+/g, "-")}`;

const PRICE_TRENDS = {
  up: { symbol: "↑", label: "Cena vzrostla" },
  down: { symbol: "↓", label: "Cena klesla" },
  same: { symbol: "→", label: "Cena se nezměnila" },
};

const formatPreviousPrice = (value, currency) => {
  if (value === null || value === undefined || value === "") return null;

  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return number.toFixed(currency === "EUR" ? 3 : 2);
};

const resolvePriceTrend = ({ price_trend: priceTrend, value, previous_value: previousValue }) => {
  if (PRICE_TRENDS[priceTrend]) return priceTrend;

  if (
    value === null ||
    value === undefined ||
    value === "" ||
    previousValue === null ||
    previousValue === undefined ||
    previousValue === ""
  ) {
    return null;
  }

  const current = Number(value);
  const previous = Number(previousValue);
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;

  if (current > previous) return "up";
  if (current < previous) return "down";
  return "same";
};

const formatTrendDateTime = (value) => {
  if (!value) return "čas není uveden";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  function formatShortDateTime (value, fallback = "Bez data") {
  if (!value) return fallback;

  const raw = String(value).trim();

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:[+-]\d{2}(?::?\d{2})?)?$/
  );

  if (!match) return raw;

  const [, year, month, day, hour, minute] = match;

  return `${day}.${month}. ${hour}:${minute}`;
};


export default function StationRow({
  station,
  columns,
  isArmex,
  showHeaderLabels,
  stationIndex,
  canEdit,
  getInputValue,
  onBlurClearDraft,
  onChangeValue,
  getInputClass,
  discountRenderer,
  priceDifferenceRenderer,
  showPriceDifferenceComparison = false,
  eurAllowedFuels,
  eurFuels,
  allowedFuelsForStation,
  showLatestPriceChange = false,
  showPriceTrends = false,
}) {
  const allowedSet = useMemo(() => {
    const arr = Array.isArray(allowedFuelsForStation) ? allowedFuelsForStation : [];
    return new Set(arr);
  }, [allowedFuelsForStation]);

  const safeColumns = Array.isArray(columns) ? columns : [];
  const eurAllowedSet = eurAllowedFuels instanceof Set ? eurAllowedFuels : new Set();
  const eurFuelsSet = eurFuels instanceof Set ? eurFuels : new Set();

  const latestChange = station?.latest_price_change || null;


    
  const canShowEurInput = (fuel) =>
    stationDisplaysCurrency(station, "EUR") && eurAllowedSet.has(fuel);
  const isFuelAllowed = (fuel) => allowedSet.has(fuel);

  const getRenderedFuels = (col) => {
    const fuels = Array.isArray(col.fuels) ? col.fuels : [];

    return fuels.filter((fuel) => {
      if (!isArmex && TF_FUELS.has(fuel)) return false;
      if (isArmex && TF_FUELS.has(fuel) && !isFuelAllowed(fuel)) return false;
      return true;
    });
  };

  const getColumnSlotCount = (col) => {
    const fuels = getRenderedFuels(col);

    if (col.key === "Kurz" || fuels.some((fuel) => KURZ_GROUP.has(fuel))) {
      return Math.max(fuels.length, 1);
    }

    return fuels.some((fuel) => eurFuelsSet.has(fuel)) ? 2 : 1;
  };

  const renderCell = ({ fuel, currency, showCurLabelTop }) => {
    const topLabel = fuel === "Kurz" && currency === "CZK" ? "Kurz" : currency;

    if (!stationDisplaysCurrency(station, currency)) {
      return (
        <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field pricsF-empty-cell">
          {showCurLabelTop && <small className="currency-label">{topLabel}</small>}
          <div className="pricsF-empty-placeholder" />
        </div>
      );
    }

    if (currency === "EUR" && !canShowEurInput(fuel)) {
      return (
        <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field pricsF-empty-cell">
          {showCurLabelTop && <small className="currency-label">EUR</small>}
          <div className="pricsF-empty-placeholder" />
        </div>
      );
    }

    const data = station?.prices?.[fuel]?.[currency];

    if (!data) {
      return (
        <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field pricsF-empty-cell">
          {showCurLabelTop && <small className="currency-label">{topLabel}</small>}
          <div className="pricsF-empty-placeholder" />
        </div>
      );
    }
    const inputValue = getInputValue(
      station.id,
      fuel,
      currency,
      data?.value
    );
    const priceTrend = resolvePriceTrend(data);
    const trend = PRICE_TRENDS[priceTrend];
    const previousPrice = formatPreviousPrice(data.previous_value, currency);
    const showTrend = showPriceTrends && trend && previousPrice !== null;
    const trendDescription = showTrend
      ? `${trend.label}: ${previousPrice} ${currency} → ${formatPreviousPrice(data.value, currency)} ${currency}. Předchozí cena zaznamenána ${formatTrendDateTime(data.previous_recorded_at)}.`
      : "";

    return (
      <div key={`${fuel}-${currency}`} className="price-product-slot pricsF-price-field">
        {(showCurLabelTop || !showHeaderLabels) && (
          <small className="currency-label">{topLabel}</small>
        )}

        <input
          type="text"
          inputMode="decimal"
          value={inputValue}
          readOnly={!canEdit || (isArmex && currency === "EUR")}
          onChange={(e) =>
            onChangeValue({
              stationId: station.id,
              fuelType: fuel,
              currency,
              rawValue: e.target.value,
              isArmex,
            })
          }
          onBlur={() => onBlurClearDraft(station.id, fuel, currency)}
          className={`pricsF-fuel-input ${getInputClass({
            station,
            fuel,
            currency,
            value: data?.value,
            confirmed: data?.confirmed,
            isArmex,
          })} ${
            isArmex &&
            station?.has_active_discount &&
            Number(station?.discount_czk) > 0 &&
            !KURZ_GROUP.has(fuel) &&
            (currency === "CZK" || currency === "EUR")
              ? `input-has-discount input-has-discount--${currency.toLowerCase()}`
              : ""
          }`}
        />

        {showTrend && (
          <span
            className={`price-trend-indicator price-trend-indicator--${priceTrend}`}
            title={trendDescription}
            aria-label={trendDescription}
          >
            <span className="price-trend-indicator__arrow" aria-hidden="true">
              {trend.symbol}
            </span>
            <span>z {previousPrice}</span>
          </span>
        )}
        {priceDifferenceRenderer
          ? priceDifferenceRenderer({
              station,
              fuel,
              currency,
              value: inputValue,
              data,
            })
          : null}
        {discountRenderer ? discountRenderer({ station, fuel, currency, data }) : null}
      </div>
    );
  };

  const renderFuelBlock = ({ col, fuel, wantsEurSlot }) => {
    const currs = wantsEurSlot ? ["CZK", "EUR"] : ["CZK"];

    const showSubLabel =
      (col.key === "Kurz" && fuel !== "Kurz") ||
      (showHeaderLabels && TF_FUELS.has(fuel));

    return (
      <div
        key={fuel}
        className={`price-product-subfuel pricsF-subfuel ${
          !station?.prices?.[fuel] ? "is-missing-fuel" : ""
        }`}
      >
        {showSubLabel && <small className="subfuel-label">{fuel}</small>}

        <div className="price-product-subfuel__cells pricsF-currency-row">
          {currs.map((currency) => {
            const showCurLabelTop =
              showHeaderLabels &&
              stationIndex === 0 &&
              fuel === (col.fuels?.[0] ?? fuel);

            return renderCell({ fuel, currency, showCurLabelTop });
          })}
        </div>
      </div>
    );
  };

  return (
    <div
        className={[
          "pricsF-station-block",
          "price-station-row-block",
          !isArmex && showPriceDifferenceComparison
          ? "price-station-row-block--comparison"
          : ""
        ].join(" ")}
      >
      <div className="pricsF-station-row price-row-grid">
        <div className="pricsF-station-name price-row-grid__station station-name-stack">
          <strong>{station.name}</strong>
          
            {showLatestPriceChange && latestChange && (
              <span
                className="station-latest-change-chip"
                title={
                  latestChange?.updated_by_username
                    ? `Změnil: ${latestChange.updated_by_username}`
                    : "Poslední změna ceny"
                }
              >
                {formatShortDateTime(latestChange.changed_at)}
              </span>
            )}
        </div>
           
        <div className="pricsF-prices-row price-row-grid__products">
          {safeColumns.map((col) => {
            const renderedFuels = getRenderedFuels(col);
            const slotCount = getColumnSlotCount(col);

            return (
              <div
                key={col.key}
                className={`price-product-group ${fuelClass(col.key)}`}
                style={{ "--product-slots": slotCount }}
              >
                {!showHeaderLabels && (
                  <label className="fuel-label price-product-group__mobile-label">
                    {col.label}
                  </label>
                )}

                <div className="price-product-group__slots">
                  {renderedFuels.map((fuel) => {
                    const wantsEurSlot = eurFuelsSet.has(fuel);
                    return renderFuelBlock({ col, fuel, wantsEurSlot });
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
